import type { Hub } from '@sentry/browser';
import { ClientOptions, User, Context, Options, Breadcrumb } from './ExceptionsService';
import { MockSentryExceptionsService } from './MockSentryExceptionsService';

const mockExceptionsService = new MockSentryExceptionsService();

/**
 * @deprecated Use service constructor instead.
 *
 * @param {Object} options
 * @param {string} options.dsn - DSN value of Sentry project
 * @param {string} options.release - Release version tag (e.g. '1.483.1')
 * @param {string} options.environment - Environment ('live', 'stg01' etc.)
 * @param {string} options.application - Application type ('appjs-app', 'appjs-session-view`)
 */
export function init(options: ClientOptions) {
  console.log('ExceptionsService.init', options);
}

export function initWithHub(hub: Hub) {
  console.log('ExceptionsService.initWithHub', hub);
}

/**
 * Sets an application user information.
 *
 * @param {Object} user
 * @param {string} user.email
 * @param {string} [user.id]
 * @param {string} [user.name]
 */
export function setUser(user: User) {
  mockExceptionsService.setUser(user);
}

/**
 * Set tags and/or extra data that will be attached to all events.
 *
 * Tags are indexed and can be used for filtering events.
 * Extra is an additional data stored with an event.
 *
 * @param {Object} context
 * @param {Object} [context.tags] - Tags as key/value pairs
 * @param {Object} [context.extra] - Additional data as key/value
 */
export function setContext(context: Context) {
  mockExceptionsService.setContext(context);
}

/**
 * Capture an exception as Sentry event.
 *
 * Passed tags and extra will be set for this particular event only.
 *
 * @param {Error|string} error An error to capture
 * @param {Object} [options]
 * @param {Object} [options.tags] - Tags as key/value pairs
 * @param {Object} [options.extra] - Additional data as key/value
 * @param {Severity} [options.level=ERROR] - Severity level
 */
export function captureException(error: Error | string, options?: Options) {
  mockExceptionsService.captureException(error, options);
}

/**
 * Capture a message as Sentry event.
 *
 * @param {string} message A message to capture
 * @param {Object} [options]
 * @param {Object} [options.tags] - Tags as key/value pairs
 * @param {Object} [options.extra] - Additional data as key/value
 * @param {Severity} [options.level=INFO] - Severity level
 */
export function captureMessage(message: string, options?: Options) {
  mockExceptionsService.captureMessage(message, options);
}

/**
 * Sends a breadcrumb.
 * It's used as a trail of events which happened prior to an issue.
 *
 * @param {Object} breadcrumb
 * @param {string} breadcrumb.category - Category (e.g. 'fetch')
 * @param {string} breadcrumb.message - Message (e.g. 'GET https://api.pandadoc.com/properties [200]')
 * @param {Severity} [breadcrumb.level=INFO] - Severity level
 */
export function addBreadcrumb(breadcrumb: Breadcrumb) {
  mockExceptionsService.addBreadcrumb(breadcrumb);
}
